/**
 * The workspace's home page: one of its own sidebar pages.
 *
 * Saved as the page's sidebar id (e.g. `amplify-scorecard`, `copilot`), not as
 * an address — an id survives a rename or a new address. Workspaces set up
 * before sidebars were per workspace saved one of three words; those still
 * work. Everything that sends someone "home" asks here, so the login, the logo
 * and the breadcrumb always agree.
 */
import type { SelectOption } from '../ui-kit';
import type { NavLink, NavSection } from './types';

const FALLBACK = '/copilot';

// The three words the old setting saved, as the sidebar ids they meant.
const OLD_CHOICES: Record<string, string> = { copilot: 'copilot', dashboards: 'dashboard', spaces: 'spaces' };

/** A saved choice as a sidebar id, reading the old three words too. */
export function homeId(saved: string | null | undefined): string {
  if (!saved) return 'copilot';
  return OLD_CHOICES[saved] ?? saved;
}

function everyLink(sections: NavSection[]): Array<{ link: NavLink; where: string }> {
  const out: Array<{ link: NavLink; where: string }> = [];
  for (const section of sections) {
    for (const item of section.items) {
      if (item.kind === 'group') {
        item.items.forEach((link) => out.push({ link, where: `${section.label} · ${item.label}` }));
      } else {
        out.push({ link: item, where: section.label });
      }
    }
  }
  return out;
}

/**
 * Where "home" is for this workspace. A page no longer in its sidebar — hidden
 * by a sidebar edit, or its feature switched off — falls back to Copilot
 * rather than to an address that goes nowhere.
 */
export function homePath(sections: NavSection[], saved: string | null | undefined): string {
  if (saved === 'chat') return '/chat';
  const id = homeId(saved);
  return everyLink(sections).find(({ link }) => link.id === id)?.link.path ?? FALLBACK;
}

/**
 * The pages a workspace can choose as its home, grouped as its sidebar groups
 * them. Left out: rows not drawn in the sidebar, and pages only some people may
 * open (Settings, People) — the home page is the same for everyone.
 */
export function homeChoices(sections: NavSection[]): SelectOption[] {
  return everyLink(sections)
    .filter(({ link }) => !link.hidden && !link.requires_capability)
    .map(({ link, where }) => ({ value: link.id, label: link.label, group: where }));
}
